import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Container,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Login as LoginIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { adminLogin, clearAdminError } from '../../store/slices/adminSlice';

const validationSchema = Yup.object({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
});

const AdminLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, error } = useSelector((state: RootState) => state.admin);

  const [showPassword, setShowPassword] = useState(false);

  // Clear any stale errors when navigating to the login page
  useEffect(() => {
    dispatch(clearAdminError());
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await dispatch(adminLogin(values)).unwrap();
        navigate('/admin/dashboard', { replace: true });
      } catch (error) {
        // Error handled by Redux
      }
    },
  });

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      background: 'linear-gradient(135deg, #F8F8FC 0%, #E8F4FD 100%)',
    }}>
      {/* Left decorative panel - hidden on mobile */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        flex: 1,
        background: 'linear-gradient(135deg, #09486D 0%, #11C1EC 100%)',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: 6,
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative circles */}
        <Box sx={{ position: 'absolute', top: -60, left: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        <Box sx={{ position: 'absolute', bottom: -80, right: -40, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />
        <Box sx={{ position: 'absolute', top: '40%', right: -20, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />

        <Box
          component="img"
          src="https://mal-pay.com/images/logo22.png"
          alt="MalPay"
          sx={{ height: 60, mb: 3, filter: 'brightness(10)' }}
          onError={(e: any) => { e.target.style.display = 'none'; }}
        />
        <Typography variant="h3" sx={{ color: 'white', fontWeight: 800, mb: 2, textAlign: 'center' }}>
          Admin Portal
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', textAlign: 'center', maxWidth: 400, lineHeight: 1.8 }}>
          Manage users, merchants, transactions, and system health with complete control.
        </Typography>
      </Box>

      {/* Right login form */}
      <Box sx={{
        flex: { xs: 1, md: '0 0 520px' },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: { xs: 3, sm: 6 },
      }}>
        <Container maxWidth="sm" sx={{ width: '100%', maxWidth: 420 }}>
          {/* Logo for mobile */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 4 }}>
            <Box
              component="img"
              src="https://mal-pay.com/images/logo22.png"
              alt="MalPay"
              sx={{ height: 50 }}
              onError={(e: any) => { e.target.style.display = 'none'; }}
            />
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#202647', mb: 1 }}>
              Admin Access
            </Typography>
            <Typography variant="body1" sx={{ color: '#7F839C' }}>
              Sign in to the MalPay admin portal
            </Typography>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: 3 }}
              onClose={() => dispatch(clearAdminError())}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={formik.handleSubmit}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#272A3A', mb: 0.5 }}>
              Email Address
            </Typography>
            <TextField
              fullWidth
              id="email"
              name="email"
              placeholder="admin@example.com"
              type="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: '#B0B0C0' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2.5 }}
            />

            <Typography variant="body2" sx={{ fontWeight: 600, color: '#272A3A', mb: 0.5 }}>
              Password
            </Typography>
            <TextField
              fullWidth
              id="password"
              name="password"
              placeholder="Enter your password"
              type={showPassword ? 'text' : 'password'}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: '#B0B0C0' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff sx={{ color: '#B0B0C0' }} /> : <Visibility sx={{ color: '#B0B0C0' }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              startIcon={!isLoading && <LoginIcon />}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 700,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #09486D 0%, #11C1EC 100%)',
                boxShadow: '0 4px 15px rgba(9, 72, 109, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #07395A 0%, #0EAED6 100%)',
                  boxShadow: '0 6px 20px rgba(9, 72, 109, 0.4)',
                },
              }}
            >
              {isLoading ? 'Signing In...' : 'Admin Sign In'}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="body2" sx={{ color: '#7F839C' }}>
                Not an admin account? Please use the{' '}
                <Typography component="span" variant="body2" sx={{ color: '#11C1EC', fontWeight: 700 }}>
                  main login
                </Typography>
              </Typography>
            </Box>
          </form>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#B0B0C0', fontSize: '0.8rem' }}>
              Secure admin portal powered by MalPay
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default AdminLoginPage;
